import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Store } from '@ngxs/store';
import { BaseSharedState } from 'src/app/shared-resources/store/base.state';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-history-differance-view',
    standalone: false,
    templateUrl: './history-differance-view.component.html',
    styleUrl: './history-differance-view.component.less'
})
export class HistoryDifferanceViewComponent implements OnInit, OnDestroy, OnChanges {
    @Input() data: any; // Difference data to display
    @Input() rdmKeys: string[] = ['kc-profile-risk-setting']; // RDM keys to use for label lookup

    loading = false;
    id: any = '';
    prevId: any = '';
    mainData: any;
    categoryCollapse: any[] = [];
    historyId: any = '';
    public rdmData$ = new BehaviorSubject<any>(null);
    rdmData: any = null;

    categoryNames: any[] = [];
    indicators: Map<string, string> = new Map();
    isAmendHistory: boolean = false;
    rdmValueMap: Map<string, any[]> = new Map(); // RDM data for formatting oldValue/newValue
    fieldFormatMap: Map<string, any> = new Map(); // field format/config info

    // Pagination property
    pageSize = 10;
    constructor(private http: HttpClient,
        private cdr: ChangeDetectorRef,
        @Optional() private modalRef: NzModalRef,
        private store: Store,
        private translate: TranslateService
    ) { }

    ngOnDestroy(): void {

    }

    ngOnChanges(changes: SimpleChanges): void {
        // Handle @Input() data changes when used as embedded component
        if (changes['data'] && changes['data'].currentValue) {
            this.categoryCollapse = []; // Reset collapse state
            this.processData(changes['data'].currentValue);
        }
    }

    ngOnInit(): void {
        // If used as embedded component (no modalRef), data comes via @Input
        if (!this.modalRef) {
            if (this.data) {
                this.processData(this.data);
            }
            return;
        }

        // Check if data is passed from modal config
        const modalData = this.modalRef.getConfig().nzData;

        // Get rdmKeys from modal config if provided
        if (modalData?.rdmKeys) {
            this.rdmKeys = modalData.rdmKeys;
        }

        // If difference data is provided directly, use it
        if (modalData?.data) {
            if (modalData.isAmendHistory) {
                this.isAmendHistory = modalData.isAmendHistory;
                this.indicators = modalData.indicator;
            }
            if (modalData.rdmValueMap) {
                this.rdmValueMap = modalData.rdmValueMap;
            }
            if (modalData.fieldFormatMap) {
                this.fieldFormatMap = modalData.fieldFormatMap;
            }
            this.data = modalData.data;
            this.processData(this.data);
        }
        // Otherwise, check for historyId and load data from API (existing functionality)
        else {
            this.historyId = modalData?.historyId;
            if (this.historyId) {
                this.loadData();
            }
        }
    }

    /**
     * Process data when provided directly (not loaded from API)
     */
    processData(data: any): void {
        this.loading = true;
        this.cdr.detectChanges();

        // Handle different response structures
        if (data?.content) {
            this.categoryNames = data.content.categoryNames;
            this.mainData = data.content.categories;
        } else if (data?.categoryNames && data?.categories) {
            this.categoryNames = data.categoryNames;
            this.mainData = data.categories;
        }

        this.loading = false;
        this.cdr.detectChanges();
        this.initStoreData();
    }

    loadData(): void {
        this.loading = true;
        const url = `${environment.baseUrl}/intelligenceman/api/v1/riskconfig/risk-config-difference?id=${this.historyId}`;
        this.http.get<any>(url).subscribe({
            next: (response) => {
                this.loading = false;
                this.cdr.detectChanges();
                this.categoryNames = response?.content?.categoryNames;
                this.mainData = response?.content?.categories;
                this.initStoreData();
            },
            error: () => {
                this.categoryNames = [];
                this.loading = false;
                this.cdr.detectChanges();
            }
        });
    }

    visibelChild(data: any) {
        if (data?.childs?.length && data?.hasData) {
            data.isVisibel = !data.isVisibel;
            if (!data.isVisibel) {
                this.unvisibelChild(data);
            }
        }
    }

    unvisibelChild(data: any) {
        const hide = (node: any) => {
            node.isVisibel = false;
            node?.childs?.forEach((child: any) => hide(child));
        };
        data?.childs?.forEach((child: any) => hide(child));
    }

    async initStoreData() {
        try {
            const rdmData = await firstValueFrom(this.store.select(BaseSharedState.getRdmData));
            this.rdmData$.next(rdmData);
            this.rdmData$.subscribe(res => {
                this.rdmData = res;
                this.setLabels();
            });
        } catch (error) {
            console.error('Error loading RDM data:', error);
        }
    }

    setLabels() {
        this.categoryCollapse = []; // Reset to prevent duplicates on re-render
        this.categoryNames?.map((name: any, index: number) => {

            // Skip entire category if no actual changes exist
            const categoryItems: any[] = this.mainData[name] || [];
            const categoryHasData = categoryItems.some((item: any) => this.hasActualChanges(item));
            if (!categoryHasData) {
                this.categoryCollapse.push(null); // keep index alignment
                return;
            }

            let label = this.isAmendHistory ? undefined : this.getLabelFromRdm(name);

            if (!label) {
                if (this.isAmendHistory) {
                    label = this.indicators.get(name);
                    if (!label) { label = name; }
                } else {
                    label = this.formatFieldName(name);
                }
            }

            this.categoryCollapse.push({ name: label, isVisibel: index == 0 });

            categoryItems.map((category: any, mainIndex: number) => {
                this.setLabel(category);
                category.isVisibel = mainIndex == 0;
                category.hasData = this.hasActualChanges(category);
                this.initChildLabels(category?.childs);
            });
        });
    }

    initChildLabels(childs: any[]) {
        if (!childs?.length) return;
        childs.forEach((child: any) => {
            this.setLabel(child);
            child.isVisibel = false;
            child.hasData = this.hasActualChanges(child);
            this.initChildLabels(child?.childs);
        });
    }

    hasActualChanges(node: any): boolean {
        if (!node) return false;
        if (!node.isCollection && (node.oldValue != null || node.newValue != null)) return true;
        return node?.childs?.some((child: any) => this.hasActualChanges(child)) ?? false;
    }

    setLabel(category: any) {
        const fieldName = category?.fieldName;
        const entityName = category?.entityName;
        if (!fieldName && !entityName) return;

        let label = !this.isAmendHistory ? this.getLabelFromRdm(fieldName) : undefined;

        if (!label) {
            if (this.isAmendHistory) {
                label = this.indicators.get(fieldName);
                if (!label) {
                    label = fieldName;
                }
            } else {
                if (category?.description) {
                    const formattedOld = this.formatValueWithRdm(category?.description, fieldName);
                    this.translate.get(formattedOld).subscribe((t: string) => {
                        category.description = (t && t !== formattedOld) ? t : formattedOld;
                        this.cdr.detectChanges();
                    });
                }
                const openAs = this.modalRef.getConfig()?.nzData?.openAs ?? '';
                if(openAs === "roleHistory"){
                    const addDecription = category?.description ? " - " + category?.description : ''
                    label = this.formatFieldName(fieldName || entityName) + addDecription;
                }else{
                    label = this.formatFieldName(fieldName || entityName);
                }
            }
        }

        if (label) category.label = label;

        // Format oldValue/newValue using RDM or boolean formatting
        if (!category.isCollection) {
            if (category.oldValue) {
                const formattedOld = this.formatValueWithRdm(category.oldValue, fieldName);
                this.translate.get(formattedOld).subscribe((t: string) => {
                    category.oldValue = (t && t !== formattedOld) ? t : formattedOld;
                    this.cdr.detectChanges();
                });
            }
            if (category.newValue) {
                const formattedNew = this.formatValueWithRdm(category.newValue, fieldName);
                this.translate.get(formattedNew).subscribe((t: string) => {
                    category.newValue = (t && t !== formattedNew) ? t : formattedNew;
                    this.cdr.detectChanges();
                });
            }
        }
    }

    formatValueWithRdm(value: string, fieldName: string): string {
        if (!value) return value;
        const key: string = fieldName?.split('.').pop() ?? fieldName;
        const col = this.fieldFormatMap?.get(key);

        // Special path handling
        if (col?.path === 'declarationSystem.cdValuationRetained' || col?.path === 'itemSystem.isValuationRetained') {
            return value === 'true' || value === '1' ? '1 - Valuation results retained during amendment' : '0 - Valuation results not retained';
        }
        if (col?.path === 'declarationSystem.cdValuationResult') {
            return value === 'true' || value === '1' ? '1 - Valuation result generated' : '0 - No valuation result generated';
        }
        if (col?.path === 'itemSystem.isValuationResult') {
            return value === 'true' || value === '1' ? '1 - Valuation result available' : '0 - No valuation result';
        }
        if (col?.path === 'itemSystem.isValuationMethod') {
            return value === 'true' || value === '1' ? '1 - Profile Hit' : '0 - No Profile Hit';
        }

        // yesNo format
        if (col?.format === 'yesNo') {
            return value === 'true' || value === '1' ? 'Yes' : 'No';
        }

        // boolean format (1=Yes, 2=No)
        if (col?.format === 'boolean') {
            return value == '1' ? 'Yes' : value == '2' ? 'No' : value;
        }

        // date format
        if (col?.format === 'date' && value) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}-${month}-${year}`;
                }
            } catch (e) { }
        }

        // RDM code-label lookup
        if (this.rdmValueMap?.size > 0) {
            const rdmList = this.rdmValueMap.get(key);
            if (rdmList && Array.isArray(rdmList)) {
                const found = rdmList.find((item: any) => item.code === value || item.code == value);
                if (found?.label) {
                    return `${value} - ${found.label}`;
                }
            }
        }

        // Generic boolean
        if (value === 'true' || value === 'false') {
            return value === 'true' ? 'Yes' : 'No';
        }
        return value;
    }

    /**
     * Get label from RDM data using the configured rdmKeys
     */
    getLabelFromRdm(code: string): string | null {
        if (!this.rdmData || !code) return null;

        for (const key of this.rdmKeys) {
            const rdmList = this.rdmData.get(key);
            const found = rdmList?.find((x: any) => x?.code == code);
            if (found?.label) {
                return found.label;
            }
        }
        return null;
    }

    /**
     * Convert camelCase to Title Case
     * e.g., "accessDetails" -> "Access Details"
     */
    formatFieldName(fieldName: string): string {
        if (!fieldName) return '';
        return fieldName
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
            .trim();
    }
}
